import { Fragment, useState, useEffect } from "react";
import { apiService, type AnalyticsHeatmapResponse, type AnalyticsFilterParams } from "../../services/api.service";

interface Props {
    filters: AnalyticsFilterParams;
}

function completionColor(pct: number): string {
    if (pct >= 75) return "#10b981";
    if (pct >= 50) return "#fbbf24";
    if (pct >= 25) return "#f59e0b";
    return "#ef4444";
}

export const AnalyticsHeatmap = ({ filters }: Props) => {
    const [data, setData] = useState<AnalyticsHeatmapResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);

    useEffect(() => {
        const load = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const result = await apiService.getAnalyticsHeatmap(filters);
                setData(result);
            } catch {
                setError("Failed to load heatmap data");
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [filters]);

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <p>Loading heatmap...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <p>{error}</p>
            </div>
        );
    }

    if (!data || data.students.length === 0 || data.lessons.length === 0) {
        return <p className="text-center py-12 text-muted-foreground">No data for heatmap</p>;
    }

    // Build lookup map
    const cellMap = new Map<string, number>();
    for (const cell of data.cells) {
        cellMap.set(`${cell.user_id}-${cell.lesson_id}`, cell.avg_completion_pct);
    }

    return (
        <div className="p-4">
                <div className="overflow-x-auto">
                    <div
                        className="grid gap-0.5"
                        style={{
                            gridTemplateColumns: `140px repeat(${data.lessons.length}, 28px)`,
                            width: `${140 + data.lessons.length * 29}px`,
                        }}
                    >
                        {/* Header row */}
                        <div className="bg-muted rounded-sm" />
                        {data.lessons.map((l) => (
                            <div
                                key={l.lesson_id}
                                className="text-[11px] font-semibold text-muted-foreground text-center px-1 py-1.5 bg-muted rounded-sm truncate uppercase tracking-wider"
                                title={l.name || undefined}
                            >
                                {l.name || `L${l.lesson_id}`}
                            </div>
                        ))}

                        {/* Data rows */}
                        {data.students.map((s) => (
                            <Fragment key={s.user_id}>
                                <div className="text-sm font-medium px-2 py-1.5 bg-muted rounded-sm truncate">
                                    {s.name || `User ${s.user_id}`}
                                </div>
                                {data.lessons.map((l) => {
                                    const key = `${s.user_id}-${l.lesson_id}`;
                                    const val = cellMap.get(key);
                                    const hasData = val !== undefined;
                                    return (
                                        <div
                                            key={key}
                                            className="flex items-center justify-center min-h-[36px] rounded-sm transition-transform hover:scale-105 hover:z-10"
                                            style={{ backgroundColor: hasData ? completionColor(val) : "hsl(60 4.8% 95.9%)" }}
                                            onMouseEnter={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setTooltip({
                                                    x: rect.left + rect.width / 2,
                                                    y: rect.top - 8,
                                                    text: hasData
                                                        ? `${s.name || `User ${s.user_id}`} - ${l.name || `Lesson ${l.lesson_id}`}: ${val}%`
                                                        : `${s.name || `User ${s.user_id}`} - ${l.name || `Lesson ${l.lesson_id}`}: No data`,
                                                });
                                            }}
                                            onMouseLeave={() => setTooltip(null)}
                                        >
                                            {hasData && (
                                                <span className="text-[10px] font-bold text-white drop-shadow-sm tabular-nums">{val}%</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </Fragment>
                        ))}
                    </div>
                </div>

                {/* Legend */}
                <div className="flex items-center gap-3 mt-4 text-xs text-muted-foreground">
                    <span>0%</span>
                    <div className="flex w-28 h-3 rounded overflow-hidden">
                        <div style={{ background: "#ef4444", flex: 1 }} />
                        <div style={{ background: "#f59e0b", flex: 1 }} />
                        <div style={{ background: "#fbbf24", flex: 1 }} />
                        <div style={{ background: "#10b981", flex: 1 }} />
                    </div>
                    <span>100%</span>
                    <span className="ml-4 px-2 py-0.5 bg-muted rounded text-xs">No data</span>
                </div>

                {tooltip && (
                    <div
                        className="fixed bg-foreground text-background px-2.5 py-1 rounded text-xs font-medium pointer-events-none -translate-x-1/2 -translate-y-full whitespace-nowrap z-50"
                        style={{ left: tooltip.x, top: tooltip.y }}
                    >
                        {tooltip.text}
                    </div>
                )}
        </div>
    );
};
